import { List, Map } from 'immutable';
import values from 'lodash/values';
import { defineMessages } from 'react-intl';

import * as D from './deps';

export const emptyMap = Map();
export const emptyList = List();

export {
  BackstageLoggerDefaultArgs,
  Actions as ExternalActions,
  Keys,
} from './externals/constants';

export const BackstageActionTypes = Object.freeze({
  ANNOTATE_OBJECTS_FOR_BACKSTAGE: 'ANNOTATE_OBJECTS_FOR_BACKSTAGE',
  ANNOTATE_OBJECTS_FOR_BACKSTAGE_ARTIST_ALL_SONGS:
    'ANNOTATE_OBJECTS_FOR_BACKSTAGE_ARTIST_ALL_SONGS',
  GET_ALL_ARTIST_TRACKS_FOR_BACKSTAGE: 'GET_ALL_ARTIST_TRACKS_FOR_BACKSTAGE',
  GET_ARTIST_DISCOGRAPHY_ANNOTATIONS: 'GET_ARTIST_DISCOGRAPHY_ANNOTATIONS',
  GET_ARTIST_DISCOGRAPHY_FOR_BACKSTAGE: 'GET_ARTIST_DISCOGRAPHY_FOR_BACKSTAGE',
  GET_ARTIST_EVENTS_FOR_BACKSTAGE: 'GET_ARTIST_EVENTS_FOR_BACKSTAGE',
  GET_CATALOG_DETAILS_FOR_BACKSTAGE: 'GET_CATALOG_DETAILS_FOR_BACKSTAGE',
  MUTATE_BACKSTAGE_DATA: 'MUTATE_BACKSTAGE_DATA',
} as const);

export const BackstageConstants = Object.freeze({
  NUM_CURATOR_PLAYLISTS_TO_RENDER: 6,
  NUM_CURATOR_STATIONS_TO_RENDER: 6,
  NUM_ITEMS_PER_PAGE_ARTIST_ALL_TRACKS: 20,
  NUM_ITEMS_PER_ROW: 4,
  NUM_ITEMS_PER_ROW_LARGE: 8,
  NUM_ITEMS_PER_ROW_WITH_RIGHT_RAIL: 6,
  NUM_ITEMS_TO_RENDER_MOBILE_WEB: 3,
  NUM_SIMILAR_ARTISTS_TO_RENDER: 6,
  NUM_TOP_TRACKS_TO_RENDER: 6,
} as const);

export const BackstageStyles = Object.freeze({
  EXPANDED: 'expanded',
} as const);

export const BackstageStylesValues = values(BackstageStyles);

export const GridItemGridStyles = Object.freeze({
  BACKSTAGEPAGE: 'bsp',
} as const);

export const BackstageEvents = {
  insufficientRights: D.Constants.Rights.RightsConstants,
  pause: D.Constants.AudioPlayer.AudioPlayerActions.PAUSE,
  play: D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_PLAY_TRACK,
  share: D.Constants.SourceCard.SourceCardConstants.SHARE,
  startArtistStation: D.Constants.SourceCard.SourceCardConstants.STARTSTATIONARTIST,
};

export const BackstageSections = D.Constants.ContentSections.BackstageContentSections;

export const ArtistAllAlbumsSortTypes = Object.freeze({
  All: 'All',
  Album: 'Album',
  Single: 'Single',
  Compilation: 'Compilation',
} as const);

export const ArtistAllAlbumsSortMessages = defineMessages({
  [ArtistAllAlbumsSortTypes.All]: {
    id: 't.all',
    defaultMessage: 'All',
  },
  [ArtistAllAlbumsSortTypes.Album]: {
    id: 't.albums',
    defaultMessage: 'Albums',
  },
  [ArtistAllAlbumsSortTypes.Single]: {
    id: 't.singles',
    defaultMessage: 'Singles',
  },
  [ArtistAllAlbumsSortTypes.Compilation]: {
    id: 't.compilations',
    defaultMessage: 'Compilations',
  },
});

export const BackstageSortTypes = Object.freeze({
  Recent: 'Recent',
  AtoZ: 'AtoZ',
} as const);

export const BackstageSortMessages = defineMessages({
  [BackstageSortTypes.AtoZ]: {
    id: 't.atoz',
    defaultMessage: 'A to Z',
  },
  [BackstageSortTypes.Recent]: {
    id: 't.recent',
    defaultMessage: 'Recent',
  },
});

export const messages = defineMessages({
  discography: {
    id: 't.discography',
    defaultMessage: 'Discography',
  },
  fullDiscography: {
    id: 'id.fullDiscography',
    defaultMessage: 'See Full Discography',
  },
  viewAll: {
    id: 'id.viewAll',
    defaultMessage: 'View All',
  },
  seeAllAlbums: {
    id: 'id.seeAllAlbums',
    defaultMessage: 'See All Albums',
  },
  seeAllSongs: {
    id: 'id.seeAllSongs',
    defaultMessage: 'See All Songs',
  },
  seeAllTourDates: {
    id: 't.seeAllTourDates',
    defaultMessage: 'See All Tour Dates',
  },
  similarSongs: {
    id: 't.similarSongs',
    defaultMessage: 'Similar Songs',
  },
  fansAlsoLike: {
    id: 't.fansAlsoLike',
    defaultMessage: 'Fans Also Like',
  },
  seeAll: {
    id: 't.seeAll',
    defaultMessage: 'See All',
  },
  seeAllSimilarSongs: {
    id: 't.seeAllSimilarSongs',
    defaultMessage: 'See All Similar Songs',
  },
  seeAllLyrics: {
    id: 't.seeAllLyrics',
    defaultMessage: 'See All Lyrics',
  },
  seeAllSongCredits: {
    id: 't.seeAllSongCredits',
    defaultMessage: 'See All Song Credits',
  },
  noTrackCredits: {
    id: 't.noTrackCredits',
    defaultMessage: 'No Track Credits Available',
  },
  similarArtists: {
    id: 't.similarArtists',
    defaultMessage: 'Similar Artists',
  },
  description: {
    id: 't.description',
    defaultMessage: 'Description',
  },
  stationDescription: {
    id: 't.stationDescription',
    defaultMessage: 'This Station',
  },
  genreDescription: {
    id: 't.genreDescription',
    defaultMessage: 'Station Description',
  },
  showMore: {
    id: 't.showMore',
    defaultMessage: 'Show More',
  },
  showLess: {
    id: 't.showLess',
    defaultMessage: 'Show Less',
  },
  follow: {
    id: 't.follow',
    defaultMessage: 'Follow @',
  },
  tracks: {
    id: 't.tracks',
    defaultMessage: 'Tracks',
  },
  lyrics: {
    id: 't.lyrics',
    defaultMessage: 'Lyrics',
  },
  songCredits: {
    id: 't.songCredits',
    defaultMessage: 'Song Credits',
  },
  focusTraits: {
    id: 't.focusTraits',
    defaultMessage: 'Features of this Song',
  },
  artistRadio: {
    id: 't.artistRadio',
    defaultMessage: 'Artist Radio',
  },
  latestRelease: {
    id: 't.latestRelease',
    defaultMessage: 'Latest Release',
  },
  allSongs: {
    id: 't.allSongs',
    defaultMessage: 'All Songs',
  },
  topSongs: {
    id: 't.topSongs',
    defaultMessage: 'Top Songs',
  },
  curatorStations: {
    id: 't.curatorStations',
    defaultMessage: 'Stations By {curatorName}',
  },
  unnamedCuratorStations: {
    id: 't.unnamedCuratorStations',
    defaultMessage: 'Stations',
  },
  curatorStationSubtitle: {
    id: 't.curatorStationSubtitle',
    defaultMessage: 'Station - {curatorName}',
  },
  unnamedCuratorStationSubtitle: {
    id: 't.curatorStationSubtitle',
    defaultMessage: 'Station',
  },
  curatorStationsNumber: {
    id: 't.curatorStationsNumber',
    defaultMessage: '{number} stations',
  },
  curatorPlaylists: {
    id: 't.curatorPlaylists',
    defaultMessage: 'Playlists By {curatorName}',
  },
  unnamedCuratorPlaylists: {
    id: 't.unnamedCuratorPlaylists',
    defaultMessage: 'Playlists',
  },
  curatorPlaylistSubtitle: {
    id: 't.curatorPlaylistSubtitle',
    defaultMessage: 'Playlist - {curatorName}',
  },
  unnamedCuratorPlaylistSubtitle: {
    id: 't.unnamedCuratorPlaylistSubtitle',
    defaultMessage: 'Playlist',
  },
  curatorPlaylistsNumber: {
    id: 't.curatorPlaylistsNumber',
    defaultMessage: '{number} playlists',
  },
  GenreCuratorSubtitle: {
    id: 't.curatorGenreSubtitle',
    defaultMessage: 'Created by {curatorName}',
  },
  spotlight: {
    id: 't.spotlight',
    defaultMessage: 'Spotlight',
  },
  onTour: {
    id: 't.onTour',
    defaultMessage: 'On Tour',
  },
  allAlbums: {
    id: 't.allAlbums',
    defaultMessage: 'All Albums',
  },
  topAlbums: {
    id: 't.topAlbums',
    defaultMessage: 'Top Albums',
  },
  albums: {
    id: 't.albums',
    defaultMessage: `{albumCount, plural,
      one {Album}
      other {Albums}
    }`,
  },
  albumBy: {
    id: 't.albumBy',
    defaultMessage: 'Album by ',
  },
  fromTheAlbum: {
    id: 't.fromTheAlbum',
    defaultMessage: 'From the album ',
  },
  by: {
    id: 't.by',
    defaultMessage: 'By ',
  },
  sampleTracks: {
    id: 't.sampleTracks',
    defaultMessage: 'Sample of Tracks on this Station',
  },
  artistEvents: {
    id: 't.artistEvents',
    defaultMessage: 'Artist Events',
  },
  startStation: {
    id: 't.startStation',
    defaultMessage: 'Start Station',
  },
  radio: {
    id: 't.radio',
    defaultMessage: '{artistName} Radio',
  },
  sampleArtists: {
    id: 't.sampleArtists',
    defaultMessage: 'Sample of Artists on this Station',
  },
  releaseYearAndTotalSongs: {
    id: 't.releaseYearAndTotalSongs',
    defaultMessage: `{releaseYear} {bullet} {trackCount, plural,
      one {1 Song}
      other {# Songs}
    }`,
  },
  playRadioOnlyTrack: {
    id: 't.trackRadioOnly',
    defaultMessage: 'This song is currently only {newline} available for radio playback.',
  },
  playUnavailableTrack: {
    id: 't.trackUnavailable',
    defaultMessage: "This song isn't currently {newline} available for playback.",
  },
  playUnavailableAlbum: {
    id: 't.playUnavailableAlbum',
    defaultMessage: "This album isn't currently available for playback.",
  },
  startArtistStationBasedOnArtist: {
    id: 't.startArtistStationBasedOnArtist',
    defaultMessage: 'Start artist station based on {artistName}?',
  },
  albumStationsNotAvailable: {
    id: 't.albumStationsNotAvailable',
    defaultMessage: 'Album stations are not currently available.',
  },
  songsOnStation: {
    id: 't.songsOnStation',
    defaultMessage: 'Songs On This Station',
  },
  artistsOnStation: {
    id: 't.artistsOnStation',
    defaultMessage: 'Artists On This Station',
  },
  totalSongs: {
    id: 't.totalSongs',
    defaultMessage: `{trackCount, plural,
      one {1 song}
      other {# songs}
    }`,
  },
  albumDescription: {
    id: 't.albumDescription',
    defaultMessage: 'Album Description',
  },
  biography: {
    id: 't.biography',
    defaultMessage: 'Biography',
  },
  about: {
    id: 't.about',
    defaultMessage: 'About',
  },
  links: {
    id: 't.links',
    defaultMessage: 'Links',
  },
  readMore: {
    id: 't.readMore',
    defaultMessage: 'Read More',
  },
  social: {
    id: 't.social',
    defaultMessage: 'Social',
  },
  exclusiveContent: {
    id: 't.exclusiveContent',
    defaultMessage: 'Pandora Exclusives',
  },
  backToArtistPage: {
    id: 't.backToArtistPage',
    defaultMessage: 'Back to {artistName} artist page',
  },
  releaseType: {
    id: 't.releaseType',
    defaultMessage: 'Release Type: ',
  },
  viewBy: {
    id: 't.viewBy',
    defaultMessage: 'View by: ',
  },
  hyphen: {
    id: 't.hyphen',
    defaultMessage: '{preText} - ',
  },
});
